import { useState, useEffect } from 'react';

import styled from '@emotion/styled';
import {
    colors, maximumAge, minimumAge, approximateAgeToBirthdate,
    birthdateToApproximateAge
} from '@modernanimal/common';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router-dom';
import { useBreakpoint } from 'use-breakpoint';

import { CustomerPatientSpeciesEnum } from '../__generated__/graphql';
import PromoCodeInput from '../clientOnboarding/PromoCodeInput';
import InputField from '../components/InputField';
import PrimaryButton from '../components/PrimaryButton';
import Fonts from '../fonts';
import { DOWNLOAD_APP_LINK, HREF_TARGET, BREAKPOINTS } from '../helpers';
import Icons from '../icons';
import images from '../images';
import useWebBookingStore from '../store';
import { isNumeric } from '../utils';

type Props = {
    goToNextStep: ()=> void;
};

// TODO reduce complexity - https://modernanimal.atlassian.net/browse/CLAUDE-2235
// eslint-disable-next-line complexity
const PatientBasics = ({ goToNextStep }: Props) => {
    const {
        patient, updatePatient
    } = useWebBookingStore((state) => ({
        patient: state.patient,
        updatePatient: state.setPatient,
    }));
    const { breakpoint } = useBreakpoint(BREAKPOINTS, 'desktop');
    const isMobile = breakpoint === 'mobile';

    const [patientName, setPatientName] = useState(patient.name);
    const [patientSpecies, setPatientSpecies] = useState<CustomerPatientSpeciesEnum | null>(patient.species);
    const [isBirthdateKnown, setIsBirthdateKnown] = useState<boolean | null>(patient.isBirthdateKnown);
    const [birthdate, setBirthdate] = useState(patient.isBirthdateKnown ? patient.birthdate : '');
    const [approximateAge, setApproximateAge] = useState<{ years: string, months: string }>(
        patient.isBirthdateKnown === false
            ? birthdateToApproximateAge(patient.birthdate)
            : { years: '', months: '' }
    );
    const [isValidBirthdate, setIsValidBirthdate] = useState(true);

    const isBirthdateEntered = (isBirthdateKnown && !!birthdate && isValidBirthdate)
        || (isBirthdateKnown === false && !!(approximateAge.years || approximateAge.months));
    const isValidInfo = !!patientName && !!patientSpecies && isBirthdateEntered;

    // Promo code
    const [isPromoSectionOpen, setIsPromoSectionOpen] = useState(false);
    const [params] = useSearchParams();
    const promoCodeFromUrl = params.get('promoCode');

    useEffect(() => {
        if (promoCodeFromUrl) {
            setIsPromoSectionOpen(true);
        }
    }, [promoCodeFromUrl]);

    useEffect(() => {
        if (birthdate) {
            setApproximateAge({ years: '', months: '' });
        }
    }, [birthdate]);

    useEffect(() => {
        if (approximateAge.years || approximateAge.months) {
            setIsValidBirthdate(true);
            setBirthdate('');
        }
    }, [approximateAge]);

    const changeBirthdate = (birthdate: string) => {
        const luxonBirthdate = DateTime.fromFormat(birthdate, 'yyyy-MM-dd').toISO();
        const minBirthdate = DateTime.fromJSDate(minimumAge).toISO();
        const maxBirthdate = DateTime.fromJSDate(maximumAge).toISO();
        const isValid = luxonBirthdate <= maxBirthdate && luxonBirthdate >= minBirthdate;

        setIsValidBirthdate(isValid);
        setBirthdate(birthdate);
    };

    const handleNext = () => {
        if (!patientSpecies) return;

        // Since breed and color are species-dependent, we need to clear the breed and color
        // values if a user goes back to edit the patient species.
        let additionalPatientAttributes = {};
        if (
            patient.species
            && patientSpecies !== patient.species
            && patient.breed.id
            && patient.color.id
        ) {
            additionalPatientAttributes = {
                breed: {
                    id: '',
                    name: '',
                    isMixed: false
                },
                color: {
                    id: '',
                    name: ''
                }
            };
        }

        const exactBirthdate = isBirthdateKnown
            ? birthdate
            : approximateAgeToBirthdate(+approximateAge.years, +approximateAge.months);

        updatePatient({
            ...patient,
            name: patientName,
            species: patientSpecies,
            birthdate: exactBirthdate,
            isBirthdateKnown,
            ...additionalPatientAttributes
        });
        goToNextStep();
    };

    const selectBirthdateIsKnown = (isKnown: boolean) => setIsBirthdateKnown(
        isBirthdateKnown === isKnown ? null : isKnown
    );

    const selectSpecies = (species: CustomerPatientSpeciesEnum) => (
        setPatientSpecies(patientSpecies === species ? null : species)
    );

    const renderBirthdateSelections = () => {
        const yesIsSelected = isBirthdateKnown === true;
        const noIsSelected = isBirthdateKnown === false;
        const YesFont = yesIsSelected ? Fonts.LabelMedium : Fonts.Body;
        const NoFont = noIsSelected ? Fonts.LabelMedium : Fonts.Body;
        return (
            <BirthdateSelectionContainer>
                <BirthdateSelection isSelected={yesIsSelected} onClick={() => selectBirthdateIsKnown(true)}>
                    <YesFont style={{ color: yesIsSelected ? colors.GREY_1 : colors.GREY_3 }}>
                        Yes
                    </YesFont>
                </BirthdateSelection>
                <BirthdateSelection isSelected={noIsSelected} onClick={() => selectBirthdateIsKnown(false)}>
                    <NoFont style={{ color: noIsSelected ? colors.GREY_1 : colors.GREY_3 }}>
                        No
                    </NoFont>
                </BirthdateSelection>
            </BirthdateSelectionContainer>
        );
    };

    const renderApproximateAgeInput = () => (
        <Subbody>
            <Fonts.LabelMedium>Approximate age</Fonts.LabelMedium>
            <ApproximateAgeContainer>
                <Subbody style={{ flex: 1 }}>
                    <Fonts.LabelMedium>Years</Fonts.LabelMedium>
                    <InputField
                        placeholder="Enter Years"
                        value={approximateAge.years}
                        onChange={(years) => {
                            if (years === '' || isNumeric(years)) {
                                setApproximateAge((prevApproximateAge) => ({
                                    ...prevApproximateAge,
                                    years
                                }));
                            }
                        }}
                        maxLength={2}
                        inputMode="numeric"
                        autoFocus={!approximateAge.years && !approximateAge.months}
                    />
                </Subbody>
                <Subbody style={{ flex: 1 }}>
                    <Fonts.LabelMedium>Months</Fonts.LabelMedium>
                    <InputField
                        placeholder="Enter Months"
                        value={approximateAge.months}
                        onChange={(months) => {
                            if (months === '' || isNumeric(months)) {
                                setApproximateAge((prevApproximateAge) => ({
                                    ...prevApproximateAge,
                                    months
                                }));
                            }
                        }}
                        maxLength={2}
                        inputMode="numeric"
                    />
                </Subbody>
            </ApproximateAgeContainer>
        </Subbody>
    );

    const renderKnownBirthdateInput = () => (
        <Subbody>
            <Fonts.LabelMedium>{'What is your pet\'s date of birth?'}</Fonts.LabelMedium>
            <DateInputContainer>
                <InputField
                    type="date"
                    value={birthdate}
                    onChange={changeBirthdate}
                    isInvalid={!isValidBirthdate}
                    min={minimumAge.toLocaleDateString('en-ca')}
                    max={maximumAge.toLocaleDateString('en-ca')}
                    dataTestId="known-birthdate-input"
                    autoFocus={!!isBirthdateKnown && !birthdate}
                />
                <CalendarIcon>
                    <Icons.Calendar data-testid="calendarIcon" height={24} width={24} fill={colors.PRIMARY_PERIWINKLE} />
                </CalendarIcon>
            </DateInputContainer>
            {!isValidBirthdate && (
                <Fonts.BodySmall style={{ color: colors.RED }}>Birthdate is out of valid range</Fonts.BodySmall>
            )}
        </Subbody>
    );

    const renderSpeciesSelections = () => (
        [CustomerPatientSpeciesEnum.Dog, CustomerPatientSpeciesEnum.Cat].map((species) => {
            const speciesImage = species === CustomerPatientSpeciesEnum.Dog ? images.Dog : images.Cat;
            const name = species === CustomerPatientSpeciesEnum.Dog ? 'Dog' : 'Cat';
            const isSelected = species === patientSpecies;

            return (
                <SpeciesSelection data-testid={`${name}Option`} key={`${name}-selection`} onClick={() => selectSpecies(species)}>
                    <IconAndName>
                        <SpeciesIcon src={speciesImage} />
                        <Fonts.Body>{name}</Fonts.Body>
                    </IconAndName>
                    {isSelected ? (
                        <Icons.CircleCheck height={24} width={24} fill={colors.GREEN} />
                    ) : (
                        <EmptyCircle />
                    )}
                </SpeciesSelection>
            );
        })
    );

    const renderPromoSection = () => (
        <PromoCodeContainer isOpen={isPromoSectionOpen}>
            <PromoCodeHeaderButton
                onClick={() => setIsPromoSectionOpen(!isPromoSectionOpen)}
                isOpen={isPromoSectionOpen}
                data-testid="promo-code-header-button"
            >
                <Fonts.Body
                    style={{ color: isPromoSectionOpen ? colors.GREY_1 : colors.PRIMARY_PERIWINKLE }}
                >
                    Have a Promo Code?
                </Fonts.Body>
                <RotatingChevron isOpen={isPromoSectionOpen}>
                    <Icons.ChevronDown height={16} width={16} fill={colors.GREY_1} />
                </RotatingChevron>
            </PromoCodeHeaderButton>
            {isPromoSectionOpen && (
                <PromoCodeInput
                    promoCodeFromUrl={promoCodeFromUrl ?? ''}
                />
            )}
        </PromoCodeContainer>
    );

    return (
        <PatientBasicsContainer>
            <Body>
                <Instructions>
                    <Fonts.Heading04>
                        {'Let\'s get to know your Modern Animal'}
                    </Fonts.Heading04>
                    <Fonts.Body style={{ color: colors.GREY_2 }}>
                        {'If you need to book for multiple pets, please book and '}
                        <Link href={DOWNLOAD_APP_LINK} target={HREF_TARGET}>
                            download our app
                        </Link>
                        .
                    </Fonts.Body>
                </Instructions>
                <Subbody>
                    <Fonts.LabelMedium>Pet Name</Fonts.LabelMedium>
                    <InputField
                        value={patientName}
                        placeholder="Pet name"
                        autoFocus={!patientName}
                        onChange={setPatientName}
                        maxLength={32}
                        isInvalid={!patientName || patientName.length >= 32}
                    />
                </Subbody>
                <Subbody>
                    <Fonts.LabelMedium>Are they a dog or a cat?</Fonts.LabelMedium>
                    {renderSpeciesSelections()}
                </Subbody>
                <Subbody>
                    <Fonts.LabelMedium>{'Do you know your pet\'s date of birth?'}</Fonts.LabelMedium>
                    {renderBirthdateSelections()}
                </Subbody>
                {isBirthdateKnown && renderKnownBirthdateInput()}
                {isBirthdateKnown === false && renderApproximateAgeInput()}
                {renderPromoSection()}
            </Body>
            <NextButtonContainer isMobile={isMobile}>
                <PrimaryButton id="patients-basic-button" disabled={!isValidInfo} onClick={handleNext} color="periwinkle">
                    Next
                </PrimaryButton>
            </NextButtonContainer>
        </PatientBasicsContainer>
    );
};

export default PatientBasics;

const PatientBasicsContainer = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 32px 24px 0 24px;
    gap: 8px;
    justify-content: space-between;
`;

const ApproximateAgeContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 16px;
`;

const BirthdateSelectionContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 6px;
`;

const BirthdateSelection = styled.button<{ isSelected: boolean }>`
    display: flex;
    height: 48px;
    justify-content: center;
    align-items: center;
    flex: 1;
    border-radius: 40px;
    background-color: ${({ isSelected }) => (isSelected ? colors.WHITE : colors.GREY_6)};
    border: ${({ isSelected }) => `1px solid ${isSelected ? colors.PERIWINKLE_4 : colors.GREY_6}`};
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const CalendarIcon = styled.div`
    display: flex;
    position: absolute;
    right: 12px;
    pointer-events: none;
`;

const DateInputContainer = styled.div`
    display: flex;
    position: relative;
    align-items: center;
`;

const EmptyCircle = styled.div`
    display: flex;
    height: 22px;
    width: 22px;
    border-radius: 12px;
    border: 1px solid ${colors.PERIWINKLE_4};
`;

const IconAndName = styled.div`
    display: flex;
    flex-direction: row;
    gap: 6px;
    align-items: center;
`;

const Instructions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
`;

const Link = styled.a`
    color: ${colors.PRIMARY_PERIWINKLE};
`;

const NextButtonContainer = styled.div<{ isMobile: boolean }>`
    display: flex;
    position: ${({ isMobile }) => (isMobile ? 'sticky' : 'static')};
    bottom: 0;
    background-color: ${colors.WHITE};
    padding: 16px 0 32px 0;
`;

const SpeciesIcon = styled.img`
    height: 28px;
    width: 28px;
`;

const SpeciesSelection = styled.button`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    height: 48px;
    width: 100%;
    border-radius: 8px;
    border: 1px solid ${colors.PERIWINKLE_5};
    padding: 0 12px;
`;

const Subbody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const PromoCodeContainer = styled.div<{ isOpen: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    border: 1px solid ${colors.PERIWINKLE_4};
    border-left: none;
    border-right: none;

    ${({ isOpen }) => (isOpen && 'padding: 0 0 14px 0;')}
`;

const PromoCodeHeaderButton = styled.button<{ isOpen: boolean }>`
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: space-between;
    align-items: center;

    // Using padding instead of gap to create space between header and input field to
    // allow for a larger click area
    padding: 14px 0 14px 0;
    ${({ isOpen }) => (isOpen && 'padding-bottom: 8px;')}
`;

const RotatingChevron = styled.div<{ isOpen: boolean }>`
    display: flex;
    transition: transform 0.3s ease-in-out;
    transform: ${({ isOpen }) => (isOpen ? 'rotate(-180deg)' : 'rotate(0deg)')};
`;
